// Accept LDH domains (letters, digits, hyphen), including multi-label public suffixes like "co.uk".
// Reject obvious malformed inputs, empty labels, labels starting/ending with hyphen, all-numeric TLDs, etc.
// Allows punycode labels (e.g., xn--...).

const MAX_DOMAIN_LENGTH = 253; // RFC 1035/1123 practical limit
const MAX_LABEL_LENGTH = 63;

const extractHost = (input: string): string => {
  const trimmed = input.trim();
  if (!trimmed) {
    return '';
  }

  // If it looks like a URL, use URL() to robustly pull out the host.
  // Fallback to manual parsing if it isn't a full URL.
  try {
    const url = trimmed.match(/^[a-z]+:\/\//i)
      ? new URL(trimmed)
      : new URL(`http://${trimmed}`);
    // url.hostname is already lowercased, without port, path, or auth
    return url.hostname.replace(/\.$/, ''); // strip trailing dot from FQDNs
  } catch {
    // Manual fallback: strip scheme, auth, path/query/fragment, and port
    const withoutScheme = trimmed.replace(/^[a-z]+:\/\//i, '');
    const withoutAuth = withoutScheme.replace(/^[^@]*@/, '');
    const hostPort = withoutAuth.split(/[/?#]/)[0] || '';
    const host = hostPort.replace(/:\d+$/, ''); // drop :port
    return host.replace(/\.$/, '').toLowerCase();
  }
};

const isValidDomain = (domain: string): boolean => {
  // Guard against quick malformed cases
  if (
    !domain ||
    domain.includes('..') ||
    domain.startsWith('.') ||
    domain.endsWith('.')
  ) {
    return false;
  }

  if (domain.length > MAX_DOMAIN_LENGTH) {
    return false;
  }

  const labels = domain.split('.');
  // Require at least one dot: {second-level}.{public suffix / tld}
  if (labels.length < 2) {
    return false;
  }

  // TLD must not be all-numeric and must have length >= 2
  const tld = labels[labels.length - 1];
  if (tld.length < 2) {
    return false;
  }
  if (/^\d+$/.test(tld)) {
    return false;
  }

  // Validate each label
  for (const label of labels) {
    // length and characters
    if (!label || label.length > MAX_LABEL_LENGTH) {
      return false;
    }
    if (!/^[a-z0-9-]+$/i.test(label)) {
      return false;
    }

    // must start and end with alphanumeric (no leading/trailing hyphen)
    if (!/^[a-z0-9]/i.test(label) || !/[a-z0-9]$/i.test(label)) {
      return false;
    }

    // Optional: forbid labels that are entirely numeric (keeps pure numeric "tld" or single-label hostnames out)
    // (You can relax this if you want numeric SLDs like "123example.com" — those still pass.)
    // if (/^\d+$/.test(label)) return false;
  }

  return true;
};

export const sanitizeDomains = (domains: string[], maxDomains: number) => {
  const uniqueDomains: string[] = [];
  let hasSanitized = false;
  let hasInvalidDomains = false;

  for (const raw of domains) {
    const host = extractHost(raw);

    if (!host) {
      hasSanitized = true;
      continue;
    }

    if (!isValidDomain(host)) {
      hasInvalidDomains = true;
      continue;
    }

    if (!uniqueDomains.includes(host)) {
      uniqueDomains.push(host);
    } else {
      hasSanitized = true;
    }
  }

  const limitedDomains =
    uniqueDomains.length > maxDomains
      ? uniqueDomains.slice(0, maxDomains)
      : uniqueDomains;

  return {
    domains: limitedDomains,
    truncated: uniqueDomains.length > maxDomains,
    sanitized: hasSanitized || limitedDomains.length !== domains.length,
    hasInvalidDomains
  } as const;
};
